import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { GoogleMap, useJsApiLoader, MarkerF, MarkerClustererF, PolylineF } from '@react-google-maps/api';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Navigation, Star, X, Clock, Grape, Baby, List, SlidersHorizontal, ChevronDown } from 'lucide-react';
import { useLanguage } from '@/contexts/LanguageContext';
import { useLocation } from '@/contexts/LocationContext';
import { api } from '@/lib/api';
import { getOpenStatus } from '@/lib/openingHours';
import { TYPOLOGIES, OTHER_TYPOLOGY, TRAIL_KEYS, isMappedCategory, typologyForCategory, typologyForPlace, typologyForTrail, placeCategories, pinIconUrl, clusterStyle } from '@/lib/mapTypologies';
import { PlaceSheet, TrailSheet } from '@/components/map/MapSheets';
import { Skeleton } from '@/components/ui/skeleton';

const GOOGLE_MAPS_API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;

const mapContainerStyle = { width: '100%', height: '100%' };
const pageStyle = { height: 'calc(100vh - 72px - env(safe-area-inset-top, 0px))' };
const defaultCenter = { lat: 28.2916, lng: -16.6291 };
const DEFAULT_TRAILS = { senderos: true, bicicleta: false, pistas: false };
const TRAIL_LABELS = {
  senderos: { es: 'Senderos a pie', en: 'Hiking trails', de: 'Wanderwege' },
  bicicleta: { es: 'Bici / Caballo', en: 'Bike / Horse', de: 'Rad / Pferd' },
  pistas: { es: 'Pistas forestales', en: 'Forest tracks', de: 'Waldpisten' },
};

const LABELS = {
  es: { filters: 'Filtros', refine: 'Afinar por categoría', routes: 'Rutas', apply: 'Aplicar', featuredOnly: 'Solo rutas destacadas', openNow: 'Abiertos ahora', official: 'Solo oficiales', kids: 'Para niños', clear: 'Limpiar filtros', all: 'Todas', none: 'Ninguna', viewList: 'Ver lista', placesCount: 'lugares', routesCount: 'rutas', nothing: 'Elige qué quieres ver', refined: 'Afinado', without: 'sin', details: 'Ver detalles', trail: 'Ver sendero' },
  en: { filters: 'Filters', refine: 'Refine by category', routes: 'Routes', apply: 'Apply', featuredOnly: 'Featured routes only', openNow: 'Open now', official: 'Official only', kids: 'For kids', clear: 'Clear filters', all: 'All', none: 'None', viewList: 'View list', placesCount: 'places', routesCount: 'routes', nothing: 'Choose what to see', refined: 'Refined', without: 'without', details: 'View details', trail: 'View trail' },
  de: { filters: 'Filter', refine: 'Nach Kategorie verfeinern', routes: 'Routen', apply: 'Anwenden', featuredOnly: 'Nur empfohlene Routen', openNow: 'Jetzt geöffnet', official: 'Nur offizielle', kids: 'Für Kinder', clear: 'Filter löschen', all: 'Alle', none: 'Keine', viewList: 'Liste ansehen', placesCount: 'Orte', routesCount: 'Routen', nothing: 'Wähle, was du sehen willst', refined: 'Verfeinert', without: 'ohne', details: 'Details ansehen', trail: 'Wanderweg ansehen' },
};

const MapPage = () => {
  const { getLocalizedField, language } = useLanguage();
  const { location: userLocation, isRealLocation, refreshLocation } = useLocation();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const ml = LABELS[language] || LABELS.es;
  const lbl = (obj) => obj[language] || obj.es;

  const [places, setPlaces] = useState([]);
  const [trails, setTrails] = useState([]);
  const [categories, setCategories] = useState([]);
  const [selectedPlace, setSelectedPlace] = useState(null);
  const [selectedTrail, setSelectedTrail] = useState(null);
  const [placeFilters, setPlaceFilters] = useState({});
  const [routeFilters, setRouteFilters] = useState(() => {
    const p = searchParams.get('trails');
    if (p === 'none') return { senderos: false, bicicleta: false, pistas: false };
    if (p) return { senderos: p.includes('senderos'), bicicleta: p.includes('bicicleta'), pistas: p.includes('pistas') };
    return searchParams.get('category') ? { senderos: false, bicicleta: false, pistas: false } : DEFAULT_TRAILS;
  });
  const [featuredOnly, setFeaturedOnly] = useState(true);
  const [openNowOnly, setOpenNowOnly] = useState(searchParams.get('open') === '1');
  const [officialOnly, setOfficialOnly] = useState(searchParams.get('official') === '1');
  const [kidsOnly, setKidsOnly] = useState(searchParams.get('kids') === '1');
  const [showPanel, setShowPanel] = useState(false);
  const [showRefine, setShowRefine] = useState(false);
  const [mapRef, setMapRef] = useState(null);
  const [loading, setLoading] = useState(true);
  const initialCenterRef = useRef(userLocation || defaultCenter);
  const hasAutoCenteredRef = useRef(false);
  const userInteractedRef = useRef(false);

  const { isLoaded, loadError } = useJsApiLoader({ id: 'google-map-script', googleMapsApiKey: GOOGLE_MAPS_API_KEY });

  const mapOptions = useMemo(() => ({
    disableDefaultUI: true,
    zoomControl: true,
    zoomControlOptions: isLoaded && window.google?.maps ? { position: window.google.maps.ControlPosition.RIGHT_CENTER } : undefined,
    mapTypeControl: false,
    streetViewControl: false,
    fullscreenControl: false,
    styles: [{ featureType: 'poi', elementType: 'labels', stylers: [{ visibility: 'off' }] }],
  }), [isLoaded]);

  useEffect(() => {
    if (mapRef && userLocation && isRealLocation && !hasAutoCenteredRef.current && !userInteractedRef.current) {
      hasAutoCenteredRef.current = true;
      mapRef.panTo(userLocation);
    }
  }, [mapRef, userLocation, isRealLocation]);

  useEffect(() => {
    const load = async () => {
      try {
        const [placesRes, catsRes, trailsRes] = await Promise.all([
          api.getPlaces({ limit: 200 }), api.getCategories(), api.getTrails({ limit: 300 }),
        ]);
        setPlaces(placesRes.data);
        const cats = catsRes.data;
        setCategories(cats);
        const urlCats = (searchParams.get('category') || '').split(',').filter(Boolean);
        const init = {};
        cats.forEach(c => { init[c.slug] = urlCats.length ? urlCats.includes(c.slug) : true; });
        setPlaceFilters(init);
        setTrails(trailsRes.data.filter(t => t.start_latitude && t.start_longitude));
      } catch (error) {
        console.error('Error loading map data:', error);
      } finally {
        setLoading(false);
      }
    };
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const otherCats = useMemo(() => categories.filter(c => !isMappedCategory(c.slug)).map(c => c.slug), [categories]);
  const typologies = useMemo(() => (otherCats.length ? [...TYPOLOGIES, { ...OTHER_TYPOLOGY, categories: otherCats }] : TYPOLOGIES), [otherCats]);
  const typeCats = (t) => t.categories.filter(c => c in placeFilters);

  const isOpen = (p) => ['open', 'open24'].includes(getOpenStatus(p.opening_hours).state);
  const passesToggles = (p) => (!openNowOnly || isOpen(p)) && (!officialOnly || p.official_guachinche) && (!kidsOnly || p.kids_friendly);

  const filteredPlaces = useMemo(() => places.filter(p => {
    if (!passesToggles(p)) return false;
    if (Object.keys(placeFilters).length === 0) return true;
    return placeCategories(p).some(slug => placeFilters[slug] === true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }), [places, placeFilters, openNowOnly, officialOnly, kidsOnly]);

  const filteredTrails = useMemo(() => trails.filter(t => routeFilters[TRAIL_KEYS[t.trail_class]] && (!featuredOnly || t.featured)), [trails, routeFilters, featuredOnly]);

  const openNowCount = useMemo(() => places.filter(isOpen).length, [places]);

  const groups = useMemo(() => {
    const g = {};
    typologies.forEach(t => { g[t.key] = { t, places: [], trails: [] }; });
    filteredPlaces.forEach(p => { const t = typologyForPlace(p, placeFilters); (g[t.key] || g.other)?.places.push(p); });
    filteredTrails.forEach(tr => { g[typologyForTrail(tr).key].trails.push(tr); });
    return g;
  }, [typologies, filteredPlaces, filteredTrails, placeFilters]);

  const typeActive = (t) => typeCats(t).some(c => placeFilters[c]) || t.trailKeys.some(k => routeFilters[k]);
  const typePartial = (t) => { const on = typeCats(t).filter(c => placeFilters[c]).length; return on > 0 && on < typeCats(t).length; };
  const typeCount = (t) => places.filter(p => passesToggles(p) && typologyForPlace(p).key === t.key).length + trails.filter(tr => typologyForTrail(tr).key === t.key && (!featuredOnly || tr.featured)).length;

  const syncUrl = (pf, rf) => {
    const slugs = Object.keys(pf);
    const active = slugs.filter(s => pf[s]);
    const rfOn = Object.keys(rf).filter(k => rf[k]);
    setSearchParams(prev => {
      if (active.length && active.length < slugs.length) prev.set('category', active.join(',')); else prev.delete('category');
      const isDefault = Object.keys(DEFAULT_TRAILS).every(k => DEFAULT_TRAILS[k] === rf[k]);
      if (isDefault) prev.delete('trails'); else prev.set('trails', rfOn.length ? rfOn.join(',') : 'none');
      return prev;
    }, { replace: true });
  };

  const toggleTypology = (t) => {
    const on = !typeActive(t);
    const pf = { ...placeFilters };
    typeCats(t).forEach(c => { pf[c] = on; });
    const rf = { ...routeFilters };
    t.trailKeys.forEach(k => { rf[k] = on; });
    setPlaceFilters(pf); setRouteFilters(rf); syncUrl(pf, rf);
  };

  const togglePlaceFilter = (slug) => { const pf = { ...placeFilters, [slug]: !placeFilters[slug] }; setPlaceFilters(pf); syncUrl(pf, routeFilters); };
  const toggleRouteFilter = (k) => { const rf = { ...routeFilters, [k]: !routeFilters[k] }; setRouteFilters(rf); syncUrl(placeFilters, rf); };
  const setAllCategories = (on) => {
    const pf = {}; categories.forEach(c => { pf[c.slug] = on; });
    const rf = { senderos: on, bicicleta: on, pistas: on };
    setPlaceFilters(pf); setRouteFilters(rf); syncUrl(pf, rf);
  };

  const setFlag = (key, on) => setSearchParams(prev => { if (on) prev.set(key, '1'); else prev.delete(key); return prev; }, { replace: true });
  const toggleOpenNow = () => { const n = !openNowOnly; setOpenNowOnly(n); setFlag('open', n); };
  const toggleOfficial = () => { const n = !officialOnly; setOfficialOnly(n); setFlag('official', n); };
  const toggleKids = () => { const n = !kidsOnly; setKidsOnly(n); setFlag('kids', n); };

  const activeSlugs = Object.keys(placeFilters).filter(s => placeFilters[s]);
  const partialCategories = activeSlugs.length > 0 && activeSlugs.length < Object.keys(placeFilters).length;
  const refinedCount = typologies.filter(typePartial).length;
  const hasActiveFilters = openNowOnly || officialOnly || kidsOnly || refinedCount > 0;

  const clearFilters = () => {
    setOpenNowOnly(false); setOfficialOnly(false); setKidsOnly(false); setFeaturedOnly(true);
    const pf = {}; categories.forEach(c => { pf[c.slug] = true; });
    setPlaceFilters(pf); setRouteFilters(DEFAULT_TRAILS);
    setSearchParams({}, { replace: true });
  };

  const exploreUrl = () => {
    const q = new URLSearchParams();
    if (partialCategories) q.set('category', activeSlugs.join(','));
    if (openNowOnly) q.set('open', '1');
    if (officialOnly) q.set('official', '1');
    if (kidsOnly) q.set('kids', '1');
    const s = q.toString();
    return `/explore${s ? `?${s}` : ''}`;
  };

  const onMapLoad = useCallback((map) => { setMapRef(map); }, []);
  const handleTrailMarkerClick = async (trail) => {
    setSelectedPlace(null);
    setSelectedTrail(trail);
    try { const res = await api.getTrail(trail.id, language); setSelectedTrail(prev => (prev?.id === trail.id ? res.data : prev)); } catch (e) { console.error('Error loading trail track:', e); }
  };
  const centerOnUser = () => {
    if (mapRef && userLocation && isRealLocation) { mapRef.panTo(userLocation); mapRef.setZoom(14); } else if (!isRealLocation) { refreshLocation(); }
  };
  // Pan so the tapped pin sits in the upper part of the map, clear of the bottom card
  const focusPin = (lat, lng) => {
    if (!mapRef) return;
    const proj = mapRef.getProjection?.();
    const bounds = mapRef.getBounds?.();
    if (!proj || !bounds) { mapRef.panTo({ lat, lng }); return; }
    const zoom = mapRef.getZoom();
    const scale = 2 ** zoom;
    const center = proj.fromLatLngToPoint(mapRef.getCenter());
    const pin = proj.fromLatLngToPoint(new window.google.maps.LatLng(lat, lng));
    const height = mapRef.getDiv().clientHeight;
    const targetY = height * 0.35;
    const pinY = (pin.y - center.y) * scale + height / 2;
    const dy = pinY - targetY;
    const dx = (pin.x - center.x) * scale;
    if (Math.abs(dy) > 20 || Math.abs(dx) > mapRef.getDiv().clientWidth * 0.35) mapRef.panBy(dx, dy);
  };
  const selectPlace = (place) => { setSelectedTrail(null); setSelectedPlace(place); setShowPanel(false); focusPin(place.latitude, place.longitude); };
  const selectTrail = (trail) => { setSelectedPlace(null); setShowPanel(false); focusPin(trail.start_latitude, trail.start_longitude); handleTrailMarkerClick(trail); };
  const selectedCategoryName = selectedPlace ? getLocalizedField(categories.find(c => c.slug === placeCategories(selectedPlace)[0]), 'name') : null;

  const markerIcon = (t, variant, selected = false) => selected
    ? ({ url: pinIconUrl(t, variant), scaledSize: new window.google.maps.Size(48, 64), anchor: new window.google.maps.Point(24, 64) })
    : ({ url: pinIconUrl(t, variant), scaledSize: new window.google.maps.Size(36, 48), anchor: new window.google.maps.Point(18, 48) });
  const clusterOptions = (t) => ({ styles: [clusterStyle(t)], minimumClusterSize: 2, zoomOnClick: true, averageCenter: true, gridSize: 56, maxZoom: 15 });

  if (loadError) {
    return <div className="flex items-center justify-center" style={pageStyle}><p className="text-red-500">Error loading maps</p></div>;
  }
  if (!isLoaded || loading) {
    return <div style={pageStyle}><Skeleton className="h-full w-full" /></div>;
  }

  return (
    <div className="relative overflow-hidden" style={pageStyle} data-testid="map-page">
      {/* Top bar: filters button + typology chips (also acts as legend) */}
      <div className="absolute left-0 right-0 z-10 px-4 py-3" style={{ top: 0 }}>
        <div className="flex gap-2 items-center">
          <button
            onClick={() => setShowPanel(v => !v)}
            className={`relative shrink-0 flex items-center gap-1.5 px-3 py-2 rounded-full shadow-md text-sm transition-colors ${showPanel ? 'bg-gray-900 text-white' : 'bg-white text-gray-800 hover:bg-gray-50'}`}
            data-testid="map-filters-btn"
          >
            <SlidersHorizontal size={16} /> <span className="hidden sm:inline">{ml.filters}</span>
            {hasActiveFilters && <span className="absolute -top-0.5 -right-0.5 w-2.5 h-2.5 rounded-full bg-[#E07A3F] border-2 border-white" />}
          </button>
          <div className="flex gap-1.5 overflow-x-auto scrollbar-hide py-0.5" data-testid="map-typology-chips">
            {typologies.map(t => {
              const active = typeActive(t);
              const Icon = t.icon;
              const n = typeCount(t);
              return (
                <button
                  key={t.key}
                  onClick={() => toggleTypology(t)}
                  className="shrink-0 flex items-center gap-1.5 pl-1.5 pr-3 py-1 rounded-full shadow-md text-sm whitespace-nowrap transition-[background-color,color,transform] active:scale-95"
                  style={{ ...(active ? { backgroundColor: t.color, color: '#fff' } : { backgroundColor: '#fff', color: '#374151' }), opacity: n === 0 ? 0.55 : 1 }}
                  aria-pressed={active}
                  data-testid={`map-type-${t.key}`}
                >
                  <span className="w-6 h-6 rounded-full flex items-center justify-center" style={{ backgroundColor: active ? 'rgba(255,255,255,0.25)' : `${t.color}1A` }}>
                    <Icon size={14} color={active ? '#fff' : t.color} strokeWidth={2.4} />
                  </span>
                  {lbl(t.label)}
                  <span className="text-[11px]" style={{ opacity: 0.75 }}>{n}</span>
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {/* Filters panel */}
      {showPanel && (
        <div className="absolute left-4 right-4 z-10" style={{ top: 58 }}>
          <div className="bg-white rounded-xl shadow-lg p-3 max-w-sm flex flex-col" style={{ maxHeight: 'calc(100vh - 72px - 220px)' }} data-testid="map-filters-panel">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium text-gray-700">{ml.filters}</span>
              <button onClick={() => setShowPanel(false)} className="text-gray-400 hover:text-gray-600" data-testid="map-filters-close"><X size={16} /></button>
            </div>
            <div className="flex flex-wrap gap-1.5 mb-3" data-testid="map-quick-filters">
              <button onClick={toggleOpenNow} className={`flex items-center gap-1 px-3 py-1.5 rounded-full text-xs border transition-colors ${openNowOnly ? 'bg-emerald-600 text-white border-emerald-600' : 'bg-white text-emerald-700 border-emerald-200'}`} data-testid="map-open-now-filter">
                <Clock size={12} /> {ml.openNow} <span className={openNowOnly ? 'text-white/80' : 'text-emerald-600/70'}>({openNowCount})</span>
              </button>
              <button onClick={toggleOfficial} className={`flex items-center gap-1 px-3 py-1.5 rounded-full text-xs border transition-colors ${officialOnly ? 'bg-[#7B2D3A] text-white border-[#7B2D3A]' : 'bg-white text-[#7B2D3A] border-[#7B2D3A]/30'}`} data-testid="map-official-only-filter">
                <Grape size={12} /> {ml.official}
              </button>
              <button onClick={toggleKids} className={`flex items-center gap-1 px-3 py-1.5 rounded-full text-xs border transition-colors ${kidsOnly ? 'bg-[#38B2AC] text-white border-[#38B2AC]' : 'bg-white text-[#319795] border-[#38B2AC]/30'}`} data-testid="map-kids-only-filter">
                <Baby size={12} /> {ml.kids}
              </button>
            </div>

            <button onClick={() => setShowRefine(v => !v)} className="w-full flex items-center justify-between py-2 border-t border-gray-100 text-sm text-gray-700" data-testid="map-refine-toggle">
              <span>{ml.refine}{refinedCount > 0 && <span className="ml-2 text-[11px] text-[#E07A3F]">● {ml.refined}</span>}</span>
              <ChevronDown size={16} className={`transition-transform ${showRefine ? 'rotate-180' : ''}`} />
            </button>
            {showRefine && (
              <div className="overflow-y-auto min-h-0 -mx-1 px-1" data-testid="map-refine-list">
                <div className="flex justify-end gap-2 text-[11px] mb-1">
                  <button onClick={() => setAllCategories(true)} className="text-[#006D77] hover:underline" data-testid="map-cats-all">{ml.all}</button>
                  <button onClick={() => setAllCategories(false)} className="text-gray-400 hover:underline" data-testid="map-cats-none">{ml.none}</button>
                </div>
                <div className="space-y-1.5">
                  {categories.map(cat => {
                    const isActive = placeFilters[cat.slug] === true;
                    const t = typologyForCategory(cat.slug);
                    const count = places.filter(p => placeCategories(p).includes(cat.slug) && passesToggles(p)).length;
                    return (
                      <button key={cat.slug} onClick={() => togglePlaceFilter(cat.slug)} className={`w-full flex items-center gap-3 px-3 py-1.5 rounded-lg transition-colors ${isActive ? 'bg-gray-50 border border-gray-200' : 'bg-white'}`} data-testid={`filter-cat-${cat.slug}`}>
                        <span className="w-3 h-3 rounded-full" style={{ backgroundColor: isActive ? t.color : '#D1D5DB' }} />
                        <span className={`text-sm ${isActive ? 'text-gray-900' : 'text-gray-400'}`}>{getLocalizedField(cat, 'name')}</span>
                        <span className="ml-auto text-xs text-gray-400">{count}</span>
                      </button>
                    );
                  })}
                </div>
                <p className="text-[11px] uppercase tracking-wide text-gray-400 mt-3 mb-1">{ml.routes}</p>
                <div className="space-y-1.5">
                  {Object.keys(TRAIL_KEYS).map(cls => {
                    const k = TRAIL_KEYS[cls];
                    const on = routeFilters[k];
                    const color = k === 'senderos' ? TYPOLOGIES[2].color : TYPOLOGIES[3].color;
                    return (
                      <button key={k} onClick={() => toggleRouteFilter(k)} className={`w-full flex items-center gap-3 px-3 py-1.5 rounded-lg transition-colors ${on ? 'bg-gray-50 border border-gray-200' : 'bg-white'}`} data-testid={`filter-trail-${k}`}>
                        <span className="w-3 h-3 rounded-full" style={{ backgroundColor: on ? color : '#D1D5DB' }} />
                        <span className={`text-sm ${on ? 'text-gray-900' : 'text-gray-400'}`}>{lbl(TRAIL_LABELS[k])}</span>
                        <span className="ml-auto text-xs text-gray-400">{trails.filter(tr => tr.trail_class === cls && (!featuredOnly || tr.featured)).length}</span>
                      </button>
                    );
                  })}
                  <button onClick={() => setFeaturedOnly(v => !v)} className={`w-full flex items-center gap-3 px-3 py-1.5 rounded-lg transition-colors ${featuredOnly ? 'bg-amber-50 border border-amber-200' : 'bg-white'}`} data-testid="filter-trail-featured">
                    <Star size={14} className={featuredOnly ? 'text-amber-500' : 'text-gray-300'} fill={featuredOnly ? 'currentColor' : 'none'} />
                    <span className={`text-sm ${featuredOnly ? 'text-gray-900' : 'text-gray-400'}`}>{ml.featuredOnly}</span>
                    <span className="ml-auto text-xs text-gray-400">{trails.filter(t => t.featured).length}</span>
                  </button>
                </div>
              </div>
            )}
            <button onClick={() => setShowPanel(false)} className="w-full mt-3 py-2 text-sm font-medium text-[#006D77] hover:bg-[#006D77]/5 rounded-lg" data-testid="map-places-apply">{ml.apply}</button>
          </div>
        </div>
      )}

      {/* Active filter chips */}
      {hasActiveFilters && !showPanel && (
        <div className="absolute left-0 right-0 z-10 px-4" style={{ top: 56 }}>
          <div className="flex gap-1.5 overflow-x-auto scrollbar-hide items-center" data-testid="map-active-filters">
            {openNowOnly && (
              <button onClick={toggleOpenNow} className="flex items-center gap-1 px-3 py-1 rounded-full text-xs bg-emerald-600 text-white shadow whitespace-nowrap" data-testid="map-active-filter-open"><Clock size={11} /> {ml.openNow} <X size={11} /></button>
            )}
            {officialOnly && (
              <button onClick={toggleOfficial} className="flex items-center gap-1 px-3 py-1 rounded-full text-xs bg-[#7B2D3A] text-white shadow whitespace-nowrap" data-testid="map-active-filter-official"><Grape size={11} /> {ml.official} <X size={11} /></button>
            )}
            {kidsOnly && (
              <button onClick={toggleKids} className="flex items-center gap-1 px-3 py-1 rounded-full text-xs bg-[#38B2AC] text-white shadow whitespace-nowrap" data-testid="map-active-filter-kids"><Baby size={11} /> {ml.kids} <X size={11} /></button>
            )}
            {refinedCount > 0 && (
              <button onClick={() => { setShowPanel(true); setShowRefine(true); }} className="flex items-center gap-1 px-3 py-1 rounded-full text-xs bg-white text-gray-800 shadow whitespace-nowrap" data-testid="map-active-filter-refined">
                <SlidersHorizontal size={11} /> {ml.refined} · {ml.without} {Object.keys(placeFilters).filter(s => !placeFilters[s]).map(s => getLocalizedField(categories.find(c => c.slug === s), 'name')).filter(Boolean).join(', ')}
              </button>
            )}
            <button onClick={clearFilters} className="px-3 py-1 rounded-full text-xs bg-gray-800/80 text-white shadow whitespace-nowrap" data-testid="map-clear-filters">{ml.clear}</button>
          </div>
        </div>
      )}

      <GoogleMap
        mapContainerStyle={mapContainerStyle}
        center={initialCenterRef.current}
        zoom={10}
        onLoad={onMapLoad}
        onDragStart={() => { userInteractedRef.current = true; }}
        onClick={() => { setShowPanel(false); setSelectedPlace(null); setSelectedTrail(null); }}
        options={mapOptions}
      >
        {userLocation && isRealLocation && (
          <MarkerF
            position={userLocation}
            icon={{ path: window.google.maps.SymbolPath.CIRCLE, scale: 10, fillColor: '#38B2AC', fillOpacity: 1, strokeColor: '#ffffff', strokeWeight: 3 }}
            zIndex={1000}
          />
        )}

        {/* One clusterer per typology so bubbles never mix types */}
        {Object.values(groups).filter(g => g.places.length + g.trails.length > 0).map(({ t, places: gp, trails: gt }) => (
          <MarkerClustererF key={t.key} options={clusterOptions(t)}>
            {(clusterer) => (
              <>
                {gp.map(place => (
                  <MarkerF key={place.id} clusterer={clusterer} position={{ lat: place.latitude, lng: place.longitude }} onClick={() => selectPlace(place)} icon={markerIcon(t, 'place', selectedPlace?.id === place.id)} zIndex={selectedPlace?.id === place.id ? 900 : undefined} />
                ))}
                {gt.map(trail => (
                  <MarkerF key={`trail-${trail.id}`} clusterer={clusterer} position={{ lat: trail.start_latitude, lng: trail.start_longitude }} onClick={() => selectTrail(trail)} icon={markerIcon(t, 'trail', selectedTrail?.id === trail.id)} zIndex={selectedTrail?.id === trail.id ? 900 : undefined} />
                ))}
              </>
            )}
          </MarkerClustererF>
        ))}

        {selectedTrail?.track_coordinates && (
          <PolylineF
            path={selectedTrail.track_coordinates.map(c => ({ lat: c[1], lng: c[0] }))}
            options={{ strokeColor: typologyForTrail(selectedTrail).color, strokeOpacity: 0.9, strokeWeight: 4 }}
          />
        )}

      </GoogleMap>

      {selectedPlace && (
        <PlaceSheet place={selectedPlace} typology={typologyForPlace(selectedPlace, placeFilters)} categoryName={selectedCategoryName} onClose={() => setSelectedPlace(null)} />
      )}
      {selectedTrail && (
        <TrailSheet trail={selectedTrail} typology={typologyForTrail(selectedTrail)} onClose={() => setSelectedTrail(null)} />
      )}

      {!selectedPlace && !selectedTrail && (<>
      <button
        onClick={centerOnUser}
        style={{ bottom: 'calc(6.5rem + env(safe-area-inset-bottom, 0px))' }}
        className={`absolute right-4 w-12 h-12 rounded-full shadow-lg flex items-center justify-center transition-colors z-[5] ${isRealLocation ? 'bg-white hover:bg-gray-50' : 'bg-gray-100 hover:bg-white'}`}
        data-testid="center-on-user-btn"
        title={isRealLocation ? 'Centrar en mi ubicación' : 'Activar ubicación'}
      >
        <Navigation size={20} className={isRealLocation ? 'text-[#38B2AC]' : 'text-gray-400'} />
      </button>

      <div className="absolute left-4 flex items-center gap-2 z-[5]" style={{ bottom: 'calc(6.5rem + env(safe-area-inset-bottom, 0px))' }}>
        <div className="bg-white rounded-full px-4 py-2 shadow-lg" data-testid="map-results-count">
          <span className="text-sm font-medium text-gray-700">
            {!typologies.some(typeActive)
              ? ml.nothing
              : [`${filteredPlaces.length} ${ml.placesCount}`, filteredTrails.length ? `${filteredTrails.length} ${ml.routesCount}` : null].filter(Boolean).join(' • ')}
          </span>
        </div>
        <button onClick={() => navigate(exploreUrl())} className="flex items-center gap-1.5 bg-white rounded-full px-3 py-2 shadow-lg text-sm text-[#006D77] font-medium hover:bg-gray-50" data-testid="map-view-list-btn">
          <List size={15} /> {ml.viewList}
        </button>
      </div>
      </>)}
    </div>
  );
};

export default MapPage;
